import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { signOut } from 'firebase/auth';
import { db, storage, auth } from '../firebaseConfig';

const AdminPanel = ({ isDarkMode, categories, onCategoryAdded, onArticleAdded, currentUser, articles = [], handleDeleteArticle }) => {
  const navigate = useNavigate();
  const textareaRef = useRef(null);

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (categories.length > 0 && !category) {
      setCategory(categories[0]);
    }
  }, [categories, category]);

  const insertMarkdown = useCallback((before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = textarea.value;
    const selectedText = currentText.substring(start, end);
    const replacement = before + selectedText + after;
    const newText = currentText.substring(0, start) + replacement + currentText.substring(end);
    
    setContent(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selectedText.length);
    }, 0);
  }, []);

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    const storageRef = ref(storage, `images/${Date.now()}_${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(Math.round(progress));
      },
      (error) => {
        console.error("ატვირთვის შეცდომა:", error);
        alert("სურათი ვერ აიტვირთა ❌");
        setIsUploading(false);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setImageUrl(downloadURL);
          setIsUploading(false);
        });
      }
    );
  }, []);

  const handleEditSelect = useCallback((article) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsEditing(true);
    setEditId(article.id);
    setTitle(article.title);
    setSummary(article.summary || '');
    setContent(article.content);
    setCategory(article.category);
    setImageUrl(article.imageUrl || '');
    setUploadProgress(0);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditId(null);
    setTitle('');
    setSummary('');
    setContent('');
    setImageUrl('');
    setUploadProgress(0);
    if (categories.length > 0) {
      setCategory(categories[0]);
    }
  }, [categories]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!title.trim() || !summary.trim() || !content.trim() || !imageUrl) {
      alert('გთხოვთ შეავსოთ ყველა ველი და ატვირთოთ სურათი!');
      return;
    }
    
    const activeUser = auth?.currentUser || currentUser;
    if (!activeUser) {
      alert('ავტორიზაციის სესია ამოიწურა. გთხოვთ, თავიდან შეხვიდეთ სისტემაში!');
      navigate('/login');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const articleData = {
        title: title.trim(),
        summary: summary.trim(),
        content: content.trim(),
        category: category,
        imageUrl: imageUrl,
        userId: activeUser.uid
      };
      if (isEditing) {
        const articleRef = doc(db, 'articles', editId);
        await updateDoc(articleRef, { ...articleData, updatedAt: new Date() });
        alert('სტატია წარმატებით განახლდა! ✨');
        handleCancelEdit();
      } else {
        await addDoc(collection(db, 'articles'), { ...articleData, createdAt: new Date() });
        alert('სტატია წარმატებით გამოქვეყნდა! 🎉');
        setTitle('');
        setSummary('');
        setContent('');
        setImageUrl('');
        setUploadProgress(0);
      }
      onArticleAdded();
    } catch (error) {
      console.error("ბაზის შეცდომა:", error);
      alert('ოპერაცია ვერ შესრულდა. შეამოწმეთ უფლებები.');
    } finally {
      setIsSubmitting(false);
    }
  }, [title, summary, content, category, imageUrl, isEditing, editId, currentUser, navigate, onArticleAdded, handleCancelEdit]);

  const handleCreateCategory = useCallback(async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    try {
      await addDoc(collection(db, 'categories'), { name: newCategoryName.trim() });
      alert(`კატეგორია "${newCategoryName.trim()}" წარმატებით დაემატა!`);
      setNewCategoryName('');
      onCategoryAdded();
    } catch (error) {
      console.error("კატეგორიის შეცდომა:", error);
      alert('კატეგორია ვერ დაემატა ბაზაში.');
    }
  }, [newCategoryName, onCategoryAdded]);

  const handleLogout = useCallback(async () => {
    await signOut(auth);
    navigate('/');
  }, [navigate]);

  const inputClass = `w-full p-3 rounded-lg border transition-colors duration-200 outline-none focus:ring-2 focus:ring-blue-500 ${
    isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-black placeholder-gray-400'
  }`;
  const cardClass = `p-6 rounded-2xl shadow-xl transition-colors duration-300 ${
    isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
  }`;

  return (
    <div className={`min-h-screen py-12 px-4 transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
    }`}>
      
      <Helmet>
        <title>ადმინ პანელი | New Innovation</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-2xl mx-auto space-y-8">
        <div className={cardClass}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">🛠️ ახალი კატეგორიის შექმნა</h3>
            <button onClick={handleLogout} className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded-lg font-bold transition-colors">
              გამოსვლა 🚪
            </button>
          </div>
          
          <form onSubmit={handleCreateCategory} className="flex gap-3">
            <input type="text" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="კატეგორიის სახელი..." className={inputClass} />
            <button type="submit" className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-bold transition-colors whitespace-nowrap">
              დამატება +
            </button>
          </form>
        </div>

        <div className={cardClass}>
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-bold">
              {isEditing ? '✏️ სტატიის რედაქტირება' : 'ახალი სტატიის დამატება'}
            </h2>
            <button onClick={() => navigate('/')} className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors">
              მთავარზე
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold mb-2">სტატიის სათაური</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} placeholder="სათაური..." />
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">მოკლე აღწერა (Summary)</label>
              <textarea value={summary} onChange={(e) => setSummary(e.target.value)} className={`${inputClass} h-20 resize-none`} placeholder="მოკლე აღწერა..." />
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">სრული კონტენტი (Content)</label>
              <div className={`flex flex-wrap gap-1.5 p-2 border rounded-t-lg border-b-0 ${
                isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-300'
              }`}>
                <button type="button" onClick={() => insertMarkdown('**', '**')} className={`px-2.5 py-1 text-sm font-bold rounded hover:opacity-80 ${
                  isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border shadow-sm'
                }`}>B</button>
                <button type="button" onClick={() => insertMarkdown('*', '*')} className={`px-2.5 py-1 text-sm italic rounded hover:opacity-80 ${
                  isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border shadow-sm'
                }`}>I</button>
                <button type="button" onClick={() => insertMarkdown('### ')} className={`px-2.5 py-1 text-sm font-black rounded hover:opacity-80 ${
                  isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border shadow-sm'
                }`}>H</button>
                <button type="button" onClick={() => insertMarkdown('- ')} className={`px-2.5 py-1 text-sm rounded hover:opacity-80 ${
                  isDarkMode ? 'bg-gray-600 text-white' : 'bg-white border shadow-sm'
                }`}>• List</button>
              </div>
              <textarea ref={textareaRef} value={content} onChange={(e) => setContent(e.target.value)} className={`${inputClass} h-40 rounded-t-none`} placeholder="დაწერე სტატია..." />
            </div>
            
            <div>
              <label className="block text-sm font-semibold mb-2">კატეგორია</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
                {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">სტატიის სურათი</label>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="w-full text-sm text-gray-500" />
              {isUploading && (
                <div className="mt-3">
                  <p className="text-sm font-bold text-blue-500">ატვირთვის პროგრესი: {uploadProgress}%</p>
                </div>
              )}
              {imageUrl && !isUploading && (
                <p className="text-sm text-green-500 mt-2 font-semibold">✓ სურათი წარმატებით აიტვირთა სერვერზე</p>
              )}
            </div>

            <div className="flex gap-4">
              {isEditing && (
                <button type="button" onClick={handleCancelEdit} className="w-1/3 bg-gray-500 text-white p-3 rounded-lg font-bold">
                  გაუქმება
                </button>
              )}
              <button type="submit" disabled={isSubmitting || isUploading} className={`p-3 rounded-lg font-bold text-white transition-colors ${isEditing ? 'w-2/3 bg-amber-600 hover:bg-amber-700' : 'w-full bg-blue-600 hover:bg-blue-700'}`}>
                {isSubmitting ? 'მუშავდება...' : (isEditing ? 'შენახვა 💾' : 'გამოქვეყნება 🚀')}
              </button>
            </div>
          </form>
        </div>

        <div className={cardClass}>
          <h3 className="text-xl font-bold mb-4">📦 არსებული სტატიების მართვა</h3>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {articles.map((art) => (
              <div key={art.id} className="p-4 rounded-xl border flex justify-between items-center">
                <div className="truncate max-w-[60%]">
                  <h4 className="font-bold truncate">{art.title}</h4>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => handleEditSelect(art)} className="bg-green-600 text-white text-xs px-3 py-1.5 rounded-lg">
                    რედაქტირება ✏️
                  </button>
                  <button onClick={() => handleDeleteArticle(art.id)} className="bg-red-600 text-white text-xs px-3 py-1.5 rounded-lg">
                    წაშლა 🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;